import React, { useState } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import {
  animations,
  colors,
  spaces,
  text,
  mixins,
} from '../../../core/styles/theme/default';
import ErrorMessage from '../ErrorMessage';

const StyledRadio = styled.span`
  display: inline-flex;
  flex-direction: column;

  & + & {
    margin-left: ${rem(spaces.delta)};
  }
`;
const StyledLabel = styled.label`
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;

  &:hover {
    > span:before {
      border: 1px solid ${colors.primary};
    }
  }
`;
const StyledSpan = styled.span`
  ${text.p3};
  position: relative;
  display: flex;
  margin: 0;
  font-weight: 400;
  align-items: center;
  cursor: pointer;

  &:before {
    content: '';
    position: relative;
    z-index: 2;
    display: block;
    width: ${rem(24)};
    height: ${rem(24)};
    margin-right: ${rem(spaces.gamma)};
    border: 1px solid ${colors.nightBlue900};
    border-radius: ${rem(24)};
  }

  &:after {
    content: '';
    position: absolute;
    left: ${rem(6)};
    top: ${rem(6)};
    z-index: 3;
    width: ${rem(12)};
    height: ${rem(12)};
    border-radius: ${rem(24)};
    transition: opacity ${animations.default};
    background: ${colors.nightBlue900};
    opacity: 0;
  }
`;
const StyledRadioItem = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  width: ${rem(24)};
  height: ${rem(24)};

  &:focus + span {
    ${mixins.fakeFocus};
  }
  &:checked + span {
    &::after {
      opacity: 1;
    }
  }
  &:disabled + span {
    cursor: default;

    &::before {
      background-color: ${colors.grey300};
      border-color: ${colors.grey500};
    }
  }
`;

const Radio = ({
  value,
  label,
  isChecked,
  isDisabled,
  selectedValue = '',
  handleRadioGroupChange,
  errorText,
  ...rest
}: RadioProps) => {
  const [isSelected, setIsSelected] = useState(isChecked || false);

  const handleChange = (event: any) => {
    setIsSelected(event.target.checked);
  };

  return (
    <StyledRadio>
      <StyledLabel {...rest}>
        <StyledRadioItem
          type="radio"
          value={value}
          checked={selectedValue ? value === selectedValue : isSelected}
          disabled={isDisabled}
          onChange={
            handleRadioGroupChange ? handleRadioGroupChange : handleChange
          }
          {...rest}
        />
        <StyledSpan>{label}</StyledSpan>
      </StyledLabel>
      {errorText && <ErrorMessage text={errorText} />}
    </StyledRadio>
  );
};

export type RadioProps = {
  /** Label of the Radio Item */
  label: string;
  /** Value of the Radio Item */
  value: string;
  /** Set if Radio Item is initially checked */
  isChecked?: boolean;
  /** Set if Radio Item is disabled */
  isDisabled?: boolean;
  /** Error Text to be shown */
  errorText?: string;
  /** Value of the selected Radio Item - for internal usage */
  selectedValue?: string;
  /** Function that will get executed when changing the Radio Item */
  handleRadioGroupChange?: () => void;
};

export default Radio;
